import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createState, fetchCountriesForDropdown } from '../services/api.ts';
import { ArrowLeft, MapPin, Globe } from 'lucide-react';
import './AddState.css';

interface Country {
  id: number;
  name: string;
  code: string;
}

const AddState: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    country_id: 0,
    enable: true
  });

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const data = await fetchCountriesForDropdown();
        setCountries(data || []);
      } catch (err) {
        console.error('Error loading countries:', err);
      }
    };
    loadCountries();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setError(null);
      await createState(formData);
      navigate('/dashboard/states');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message 
        || err.response?.data?.error 
        || err.message 
        || 'Failed to create state';
      setError(errorMessage);
      console.error('Create error:', err.response?.data);
    }
  };

  return (
    <div className="add-state">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/dashboard/states')}>
          <ArrowLeft size={20} />
          Back to States
        </button>
        <h2>Add New State</h2>
      </div>

      {error && (
        <div className="error-alert">
          <span>⚠️</span>
          <p>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-section">
            <h3 className="section-title">State Information</h3>
            
            <div className="form-group">
              <label>State Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter state name"
                required
              />
            </div>

            <div className="form-group">
              <label>State Code *</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                placeholder="Enter state code (e.g., CA, NY)"
                required
              />
              <p className="field-hint">A short abbreviation for the state</p>
            </div>

            <div className="form-group">
              <label>Country *</label>
              <div className="input-with-icon">
                <Globe size={18} className="input-icon" />
                <select
                  value={formData.country_id}
                  onChange={(e) => setFormData({ ...formData, country_id: parseInt(e.target.value) })}
                  required
                >
                  <option value={0}>Select a country</option>
                  {countries.map((country) => (
                    <option key={country.id} value={country.id}>
                      {country.name} ({country.code})
                    </option>
                  ))}
                </select>
              </div>
              <p className="field-hint">Select the country this state belongs to</p>
            </div>

            <div className="form-switches">
              <div className="switch-group">
                <div className="switch-content">
                  <label className="switch-label">Active Status</label>
                  <p className="switch-description">Enable this state for selection</p>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={formData.enable}
                    onChange={(e) => setFormData({ ...formData, enable: e.target.checked })}
                  />
                  <span className="slider"></span>
                </label>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Preview</h3>
            
            <div className="preview-card">
              <div className="preview-header">
                <div className="preview-icon">
                  <MapPin size={24} />
                </div>
                <span className={`preview-status ${formData.enable ? 'active' : 'inactive'}`}></span>
              </div>
              
              <div className="preview-content">
                <div className="preview-info">
                  <h4>{formData.name || 'State Name'}</h4>
                  <p className="preview-code">{formData.code || 'CODE'}</p>
                </div>

                <div className="preview-details">
                  <div className="preview-detail-row">
                    <Globe size={16} />
                    <span className="preview-label">Country:</span>
                    <span className="preview-value">
                      {countries.find(c => c.id === formData.country_id)?.name || 'Not selected'}
                    </span>
                  </div>
                </div>

                <div className="preview-badges">
                  <span className={`preview-badge ${formData.enable ? 'active' : 'inactive'}`}>
                    {formData.enable ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            <div className="info-box">
              <h4>🗺️ State Guidelines</h4>
              <ul>
                <li>Use official state names</li>
                <li>State codes should follow standard abbreviations</li>
                <li>Ensure the country ID is correct</li>
                <li>Keep codes uppercase for consistency</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            className="btn-secondary"
            onClick={() => navigate('/dashboard/states')}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            Create State
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddState;
